import PropTypes from 'prop-types';

import {Box, Heading, Skeleton, Text} from '@salesforce/retail-react-app/app/components/shared/ui';

import {Component} from '@amblique/king-living/app/components/contentful/component';
import {HeroBannerBackgroundMedia} from '@amblique/king-living/app/components/hero-banner-background-media';
import {
  hasHeroBannerBackgroundMedia,
  hasHeroBannerVideo,
  HERO_BANNER_MOBILE_NATURAL_VIDEO_OPTIONS,
} from '@amblique/king-living/app/utils/hero-banner-video';

/**
 * Hero Banner Block (CF-01)
 *
 * @typedef {import('@chakra-ui/styled-system').SystemStyleObject} SystemStyleObject
 * @typedef {"root" | "backgroundWrapper" | "contentWrapper" | "title" | "subtitle" | "actionLinks"} HeroBannerBlockParts
 * @typedef {Partial<Record<HeroBannerBlockParts, SystemStyleObject>>} HeroBannerBlockStyles
 *
 * @param {Object} props - Component props
 * @param {Contentful.HeroBanner} props.component - HeroBanner component data
 * @param {boolean} [props.isComplete] - Whether component has all fields
 * @param {boolean} [props.isFetching] - Whether additional data is being fetched
 * @param {HeroBannerBlockStyles} [props.styles] - Component styles
 * @returns {React.ReactElement}
 */
export const HeroBannerBlockCF01 = ({component, isComplete = true, isFetching = false, styles, ...props}) => {
  const {backgroundMedia, backgroundVideoMobile, headingTitle, subtitle, cta} = component;
  const mediaFields = {backgroundMedia, backgroundVideoMobile};
  const hasBackgroundMedia = hasHeroBannerBackgroundMedia(mediaFields);
  const hasVideo = hasHeroBannerVideo(mediaFields);
  const hasInformationSection = Boolean(headingTitle || subtitle || cta);
  const isMobileNaturalVideo = hasVideo && !hasInformationSection;

  const wrapperClassName = [
    'hero-banner',
    isMobileNaturalVideo && 'hero-banner--cf01-video-no-content',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Box className={wrapperClassName} {...styles.wrapper}>
      <Box className="hero-image-wrapper" {...styles.backgroundWrapper}>
        {hasBackgroundMedia ? (
          <HeroBannerBackgroundMedia
            backgroundMedia={backgroundMedia}
            backgroundVideoMobile={backgroundVideoMobile}
            mobileVideoOptions={
              isMobileNaturalVideo ? HERO_BANNER_MOBILE_NATURAL_VIDEO_OPTIONS : undefined
            }
            {...styles.backgroundMedia}
          />
        ) : (
          <Skeleton height="100%" width="100%" />
        )}
      </Box>

      <Box className="hero-content-wrapper" {...styles.contentWrapperOuter}>
        {!isComplete && isFetching ? (
          <Skeleton height="120px" />
        ) : (
          <Box className="hero-content-wrapper-inner" {...styles.contentWrapper}>
            {headingTitle && <Heading {...styles.title}>{headingTitle}</Heading>}
            {subtitle && <Text {...styles.subtitle}>{subtitle}</Text>}
            <Box className="cta-wrapper" {...styles.ctaWrapper}>
              {cta && <Component component={cta} {...styles.actionLinks} />}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

HeroBannerBlockCF01.displayName = 'HeroBannerBlockCF01';
HeroBannerBlockCF01.propTypes = {
  component: PropTypes.object.isRequired,
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,
  styles: PropTypes.object.isRequired,
};
